import React, { useState, useEffect } from 'react';
import { parseMetadata } from '../utils/metadataParser';

const PinCard = ({ pin, position, onCardClick, onClose, isBelow = false }) => {
  const [content, setContent] = useState({
    title: '',
    firstParagraph: '',
    firstImage: null,
    loading: true,
    error: false
  });
  const [tailPosition, setTailPosition] = useState(50);

  useEffect(() => {
    const fetchAndParseMarkdown = async () => {
      try {
        const response = await fetch(`/content/${pin.markdownFile}`);
        if (!response.ok) {
          throw new Error('Failed to load content');
        }
        const markdownText = await response.text();
        
        // Parse metadata and get content without metadata section
        const { metadata, contentWithoutMetadata } = parseMetadata(markdownText);
        
        // Parse the content (without metadata) to extract title, first paragraph, and first image
        const lines = contentWithoutMetadata.split('\n');
        let title = '';
        let firstParagraph = '';
        let firstImage = null;
        
        // Extract title (first H1 after metadata)
        for (let line of lines) {
          if (line.startsWith('# ')) {
            title = line.substring(2).trim();
            break;
          }
        }
        
        // Extract first image (fixed regex to handle title attributes)
        const imageRegex = /!\[.*?\]\(([^"\s]+)(?:\s+"[^"]*")?\)/;
        const imageMatch = contentWithoutMetadata.match(imageRegex);
        if (imageMatch) {
          firstImage = imageMatch[1];
        }
        
        // Extract first paragraph (first non-empty line that's not a header or image)
        for (let line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine && 
              !trimmedLine.startsWith('#') && 
              !trimmedLine.startsWith('!') &&
              !trimmedLine.startsWith('![') &&
              trimmedLine.length > 20) { // Ensure it's a substantial paragraph
            firstParagraph = trimmedLine;
            break;
          }
        }
        
        setContent({
          title: metadata.title || title || pin.title,
          firstParagraph: metadata.description || firstParagraph || pin.description,
          firstImage,
          loading: false,
          error: false
        });
      } catch (error) {
        setContent({
          title: pin.title,
          firstParagraph: pin.description,
          firstImage: null,
          loading: false,
          error: true
        });
      }
    };

    fetchAndParseMarkdown();
  }, [pin]);

  // Calculate tail position after component mounts and when position changes
  useEffect(() => {
    if (position.pinX !== undefined && position.pinY !== undefined) {
      // Calculate where tail should be positioned to point at pin
      const tailPixelPosition = position.pinX - position.x; // Pin X relative to bubble
      const bubbleWidth = 280; // Should match CSS width
      const tailPercentage = Math.max(10, Math.min(90, (tailPixelPosition / bubbleWidth) * 100));
      setTailPosition(tailPercentage);
    }
  }, [position]);

  const handleCardClick = (e) => {
    e.stopPropagation();
    onCardClick();
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <div
      className="pin-card"
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 2000,
      }}
      onClick={handleCardClick}
    >
      <div 
        className={`pin-card-content ${isBelow ? 'bubble-below' : ''}`}
        style={{
          '--tail-position': `${tailPosition}%`
        }}
      >
        {/* Close button */}
        <button className="pin-card-close" onClick={handleCloseClick}>
          ×
        </button>
        
        {content.loading ? (
          <div className="pin-card-loading">Loading...</div>
        ) : (
          <>
            {/* Image thumbnail */}
            {content.firstImage && (
              <div className="pin-card-image">
                <img src={content.firstImage} alt={content.title} />
              </div>
            )}
            
            {/* Content */}
            <div className="pin-card-text">
              <h3 className="pin-card-title">{content.title}</h3>
              <p className="pin-card-description">
                {content.firstParagraph.length > 120 
                  ? content.firstParagraph.substring(0, 120) + '...'
                  : content.firstParagraph
                }
              </p>
              <button className="pin-card-button">Explore</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PinCard;